import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertDialog,
  Button,
  Card,
  Dialog,
  Flex,
  IconButton,
  Select,
  Separator,
  Text,
  TextArea,
  TextField,
  Box,
  Tooltip,
} from "@radix-ui/themes";
import { ArrowLeftIcon, Cross2Icon, ImageIcon, InfoCircledIcon, MobileIcon } from "@radix-ui/react-icons";
import { ToastAlert } from "components";
import { sendTestSmsTemplate } from "api/bMarketingApi";
import { useLocationContext, useProfileContext } from "context";
import { useGetAllBrands, useMarketingSmsTemplates } from "hooks";
import { useToast } from "../../context/ToastContext";
import MarketingSmsPreview from "./MarketingSmsPreview";
import {
  buildSmsBody,
  calculateSmsMetrics,
  getDefaultSmsTemplate,
} from "./marketingSmsStore";

function buildFormSignature(template) {
  return JSON.stringify({
    name: (template.name || "").trim(),
    senderName: (template.senderName || "").trim(),
    message: (template.message || "").trim(),
    attachmentImageUrl: template.attachmentImageUrl || "",
  });
}

function validateAttachmentImage(file) {
  if (!file) {
    return { valid: false, reason: "missing" };
  }

  const allowedMimeTypes = new Set(["image/png", "image/jpeg", "image/gif"]);
  const normalizedName = String(file.name || "").toLowerCase();
  const hasAllowedExtension = /\.(png|jpe?g|gif)$/.test(normalizedName);
  const hasAllowedMimeType = allowedMimeTypes.has(String(file.type || "").toLowerCase());

  if (!hasAllowedExtension || (file.type && !hasAllowedMimeType)) {
    return { valid: false, reason: "type" };
  }

  if (file.size > 1048576) {
    return { valid: false, reason: "size" };
  }

  return { valid: true };
}

export default function MarketingSmsStudio() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);
  const {
    smsTemplates,
    updateTemplateAsync,
    isLoading,
    isUpdating,
  } = useMarketingSmsTemplates(userProfile?.organisation_id);
  const isSmsStudioTemporarilyDisabled = true;

  const existingTemplate = useMemo(() => {
    return smsTemplates.find((template) => template.id === templateId) || null;
  }, [smsTemplates, templateId]);

  const [formData, setFormData] = useState(getDefaultSmsTemplate());
  const [initialSignature, setInitialSignature] = useState("");
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testPhoneError, setTestPhoneError] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [attachmentImageFile, setAttachmentImageFile] = useState(null);
  const [attachmentImagePreview, setAttachmentImagePreview] = useState("");
  const [attachmentImageName, setAttachmentImageName] = useState("");
  const [removeAttachmentImage, setRemoveAttachmentImage] = useState(false);
  const attachmentInputRef = useRef(null);

  const resetEditorState = (template, syncInitialSignature = false) => {
    const nextTemplate = {
      ...getDefaultSmsTemplate(),
      ...(template || {}),
    };

    nextTemplate.senderName = String(nextTemplate.senderName || userProfile?.organisation_name || "").trim();

    setFormData(nextTemplate);
    if (syncInitialSignature) {
      setInitialSignature(buildFormSignature(nextTemplate));
    }
    setAttachmentImageFile(null);
    setAttachmentImagePreview(nextTemplate.attachmentImageUrl || "");
    setAttachmentImageName(nextTemplate.attachmentImageName || "");
    setRemoveAttachmentImage(false);
    setTestPhoneError("");
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (isLoading || !existingTemplate) {
      return;
    }

    resetEditorState(existingTemplate, true);
  }, [existingTemplate, isLoading]);

  const senderOptions = useMemo(() => {
    const options = [];
    const seenNames = new Set();

    const addOption = (name, kind) => {
      const normalizedName = String(name || "").trim();
      if (!normalizedName) {
        return;
      }

      const key = normalizedName.toLowerCase();
      if (seenNames.has(key)) {
        return;
      }

      seenNames.add(key);
      options.push({
        value: normalizedName,
        label: normalizedName,
        kind,
      });
    };

    addOption(userProfile?.organisation_name, "Organisation");
    (brandsData || []).forEach((brand) => addOption(brand?.name, "Brand"));
    (locationsData || []).forEach((location) => addOption(location?.name, "Location"));

    const currentSenderName = String(formData.senderName || "").trim();
    if (currentSenderName && !seenNames.has(currentSenderName.toLowerCase())) {
      options.unshift({ value: currentSenderName, label: currentSenderName, kind: "Saved label" });
    }

    return options;
  }, [brandsData, formData.senderName, locationsData, userProfile?.organisation_name]);

  useEffect(() => {
    if (!formData.senderName && senderOptions.length > 0) {
      setFormData((previous) => ({
        ...previous,
        senderName: senderOptions[0].value,
      }));
    }
  }, [formData.senderName, senderOptions]);

  const renderedMessage = useMemo(() => {
    return buildSmsBody(formData.message, {
      senderName: formData.senderName,
      includeOptOut: true,
    });
  }, [formData.message, formData.senderName]);

  const hasAttachmentImage = Boolean(attachmentImagePreview);
  const smsMetrics = useMemo(() => calculateSmsMetrics(renderedMessage, { hasAttachmentImage }), [hasAttachmentImage, renderedMessage]);
  const hasUnsavedChanges = useMemo(() => {
    return buildFormSignature({ ...formData, attachmentImageUrl: attachmentImagePreview }) !== initialSignature;
  }, [attachmentImagePreview, formData, initialSignature]);

  const updateField = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const smsMetricsTooltip = hasAttachmentImage
    ? `Adding an image sends this as MMS, not SMS. The editor allows up to ${smsMetrics.segmentCharacterLimit} characters of text, including the sender label and opt-out line shown in the preview. Carrier support and pricing can still vary.`
    : `This is a text-only SMS using ${smsMetrics.encoding}. One SMS allows ${smsMetrics.singleSegmentLimit} characters, and each extra segment allows ${smsMetrics.multiSegmentLimit}. The count includes the sender label and opt-out line shown in the preview.`;

  const attachmentLabel = hasAttachmentImage ? "MMS" : `${smsMetrics.segments} SMS`;

  const handleAttachmentImageUpload = (event) => {
    const uploadedFile = event.target.files?.[0];
    const validation = validateAttachmentImage(uploadedFile);

    if (!validation.valid) {
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }

      if (validation.reason === "type") {
        setToastMessage("Upload a PNG, JPG, JPEG, or GIF image");
      } else if (validation.reason === "size") {
        setToastMessage("Image attachments must be 1MB or smaller");
      }

      return;
    }

    setAttachmentImageFile(uploadedFile);
    setAttachmentImageName(uploadedFile.name);
    setRemoveAttachmentImage(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      setAttachmentImagePreview(String(reader.result || ""));
    };
    reader.readAsDataURL(uploadedFile);
  };

  const handleAttachmentImageClear = () => {
    setAttachmentImageFile(null);
    setAttachmentImagePreview("");
    setAttachmentImageName("");
    setRemoveAttachmentImage(true);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
  };

  const triggerAttachmentImageInput = () => {
    if (attachmentInputRef.current) {
      attachmentInputRef.current.click();
    }
  };

  const handleSave = async () => {
    const cleanedTemplate = {
      name: formData.name.trim(),
      sender_name: formData.senderName.trim(),
      message: formData.message.trim(),
    };

    if (!cleanedTemplate.name || !cleanedTemplate.sender_name || !cleanedTemplate.message) {
      setToastMessage("Add a template name, sender label, and message before saving");
      return;
    }

    if (!existingTemplate || !templateId) {
      setToastMessage("SMS template not found");
      return;
    }

    if (smsMetrics.overLimit) {
      setToastMessage(`Keep the message within ${smsMetrics.segmentCharacterLimit} characters for this ${hasAttachmentImage ? "MMS" : "SMS"}`);
      return;
    }

    try {
      const hasAttachmentChanges = Boolean(attachmentImageFile) || removeAttachmentImage;
      const payload = hasAttachmentChanges ? new FormData() : cleanedTemplate;

      if (payload instanceof FormData) {
        payload.append("name", cleanedTemplate.name);
        payload.append("sender_name", cleanedTemplate.sender_name);
        payload.append("message", cleanedTemplate.message);

        if (attachmentImageFile) {
          payload.append("attachment_image", attachmentImageFile);
        }

        if (removeAttachmentImage) {
          payload.append("remove_attachment_image", "true");
        }
      }

      const savedTemplate = await updateTemplateAsync({
        templateId,
        data: payload,
      });

      setFormData(savedTemplate);
      setInitialSignature(buildFormSignature(savedTemplate));
      setAttachmentImageFile(null);
      setAttachmentImagePreview(savedTemplate.attachmentImageUrl || "");
      setAttachmentImageName(savedTemplate.attachmentImageName || "");
      setRemoveAttachmentImage(false);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
      setToastMessage("SMS template saved");
    } catch (error) {
      setToastMessage("Unable to save the SMS template right now");
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setLeaveDialogOpen(true);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/marketing/templates");
  };

  const confirmLeave = () => {
    setLeaveDialogOpen(false);
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/marketing/templates");
  };

  const handleDiscard = () => {
    setDiscardDialogOpen(true);
  };

  const confirmDiscard = () => {
    resetEditorState(existingTemplate, false);
    setDiscardDialogOpen(false);
  };

  const handleSendTest = async () => {
    const cleanedPhone = testPhone.trim();
    if (!cleanedPhone) {
      setTestPhoneError("Enter a phone number");
      return;
    }

    if (!/^\+?[0-9()\-\s]{7,}$/.test(cleanedPhone)) {
      setTestPhoneError("Enter a valid phone number");
      return;
    }

    if (!templateId || !existingTemplate) {
      setTestPhoneError("SMS template not found");
      return;
    }

    if (attachmentImageFile && attachmentImagePreview.startsWith("data:")) {
      setTestPhoneError("Save the template before sending a preview with a new image");
      return;
    }

    setIsSendingTest(true);

    try {
      const response = await sendTestSmsTemplate(templateId, {
        phone_number: cleanedPhone,
        message: formData.message,
        sender_name: formData.senderName,
      });

      setTestDialogOpen(false);
      setTestPhone("");
      setTestPhoneError("");
      setToastMessage(response.data?.message || `Test message sent to ${cleanedPhone}`);
    } catch (error) {
      const nextError = error?.response?.data?.error || "Unable to send the SMS preview right now";
      setTestPhoneError(nextError);
    } finally {
      setIsSendingTest(false);
    }
  };

  if (isSmsStudioTemporarilyDisabled) {
    return (
      <Flex direction="column" gap="5" style={{ minHeight: "100vh", padding: "24px", background: "var(--color-background)" }}>
        <Flex justify="between" align="center">
          <Button variant="soft" color="gray" onClick={() => navigate("/marketing/templates")}>Back to templates</Button>
        </Flex>
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="3"
          style={{
            flex: 1,
            border: "1px dashed var(--gray-a6)",
            borderRadius: "var(--radius-4)",
            minHeight: 320,
            padding: "var(--space-6)",
          }}
        >
          <Text size="5" weight="medium">SMS and Whatsapp features coming soon.</Text>
          <Text color="gray" align="center">This route is temporarily disabled while the SMS features are still being finalized.</Text>
        </Flex>
        <ToastAlert />
      </Flex>
    );
  }

  return (
    <>
      <Flex direction="column" p="5" gap="5" style={{ width: "100%", height: "100%", flex: "1 0 0" }}>
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <Flex gap="4" align="center" style={{ flex: "1 0 0" }}>
            <IconButton size="2" variant="soft" color="gray" onClick={handleBack}>
              <ArrowLeftIcon width={16} height={16} />
            </IconButton>
            <Flex direction="column" gap="1">
              <Text size="5" weight="bold">{formData.name || "SMS template"}</Text>
              <Text size="2" color="gray">Write the message and check the live preview.</Text>
            </Flex>
          </Flex>

          <Flex gap="2">
            <Button variant="soft" color="amber" onClick={() => setTestDialogOpen(true)} disabled={!existingTemplate}>
              <MobileIcon width="16" height="16" />
              Send a preview
            </Button>
          </Flex>
        </Flex>

        <Dialog.Root open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <Dialog.Content style={{ maxWidth: 460 }}>
            <Dialog.Title>Send SMS preview</Dialog.Title>
            <Dialog.Description size="2" color="gray">
              This simulates a test send so you can review the copy before SMS delivery endpoints are connected.
            </Dialog.Description>

            <Flex direction="column" gap="3" mt="4">
              <label>
                <Text as="div" size="2" mb="1" weight="medium">Phone number</Text>
                <TextField.Root
                  placeholder="+44 7700 900123"
                  value={testPhone}
                  onChange={(event) => {
                    setTestPhone(event.target.value);
                    setTestPhoneError("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSendTest();
                    }
                  }}
                />
                {testPhoneError ? (
                  <Text as="div" size="1" color="red" mt="1">{testPhoneError}</Text>
                ) : null}
              </label>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button onClick={handleSendTest} disabled={isSendingTest}>{isSendingTest ? "Sending..." : "Send preview"}</Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        <AlertDialog.Root open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
          <AlertDialog.Content style={{ maxWidth: 460 }}>
            <AlertDialog.Title>Leave without saving?</AlertDialog.Title>
            <AlertDialog.Description size="2">
              You have unsaved SMS edits. Leaving now will discard those changes.
            </AlertDialog.Description>

            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">Stay here</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button color="red" onClick={confirmLeave}>Leave</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>

        <AlertDialog.Root open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
          <AlertDialog.Content style={{ maxWidth: 460 }}>
            <AlertDialog.Title>Discard changes?</AlertDialog.Title>
            <AlertDialog.Description size="2">
              This will reset the SMS editor back to the last saved version.
            </AlertDialog.Description>

            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">Cancel</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button color="red" onClick={confirmDiscard}>Discard changes</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>

        <Flex gap="4" style={{ flex: "1 0 0", minHeight: 0, overflow: "hidden" }}>
          <Card style={{ width: 390, flexShrink: 0, overflow: "hidden" }}>
            <Flex direction="column" style={{ height: "100%" }}>
              <Flex direction="column" gap="4" p="4" style={{ overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">Template name</Text>
                  <TextField.Root
                    placeholder="Weekend brunch push"
                    value={formData.name}
                    onChange={(event) => updateField("name", event.target.value)}
                  />
                </Flex>

                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">Sender label</Text>
                  <Select.Root value={formData.senderName} onValueChange={(value) => updateField("senderName", value)}>
                    <Select.Trigger style={{ width: "100%" }} />
                    <Select.Content>
                      {senderOptions.map((option) => (
                        <Select.Item key={`${option.kind}-${option.value}`} value={option.value}>
                          {option.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                  <Text size="1" color="gray">The selected name is prefixed to every outgoing SMS.</Text>
                </Flex>

                <Flex direction="column" gap="2">
                  <Text size="1" weight="medium" color="gray">Message</Text>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    hidden
                    onChange={handleAttachmentImageUpload}
                  />
                  <TextArea
                    size="2"
                    rows={12}
                    placeholder="Flower sale this weekend! Details at your-website-here.com!"
                    value={formData.message}
                    onChange={(event) => updateField("message", event.target.value)}
                    style={{ minHeight: 264, resize: "none" }}
                  />
                  <Flex
                    align="center"
                    justify="between"
                    gap="3"
                    style={{ paddingInline: 2, flexWrap: "wrap", rowGap: 8 }}
                  >
                    {hasAttachmentImage ? (
                      <Flex
                        align="center"
                        gap="2"
                        style={{
                          minWidth: 0,
                          flex: "1 1 220px",
                          maxWidth: "100%",
                          flexWrap: "wrap",
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: "var(--gray-a2)",
                          border: "1px solid var(--gray-a4)",
                          rowGap: 6,
                        }}
                      >
                        <Box
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            overflow: "hidden",
                            border: "1px solid var(--gray-a5)",
                            flexShrink: 0,
                            background: "var(--gray-a2)",
                          }}
                        >
                          <img
                            src={attachmentImagePreview}
                            alt="Attachment"
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        </Box>
                        <Text
                          size="1"
                          color="gray"
                          style={{ minWidth: 0, flex: "1 1 72px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {attachmentImageName || "Image attached"}
                        </Text>
                        <Box style={{ width: 1, height: 14, background: "var(--gray-a5)", flexShrink: 0 }} />
                        <Button size="1" variant="ghost" color="gray" onClick={triggerAttachmentImageInput}>Replace</Button>
                        <Button size="1" variant="ghost" color="gray" onClick={handleAttachmentImageClear}>
                          Remove
                        </Button>
                      </Flex>
                    ) : (
                      <Button size="1" variant="ghost" color="gray" onClick={triggerAttachmentImageInput}>
                        <ImageIcon width={14} height={14} />
                        Add image
                      </Button>
                    )}
                    <Flex
                      align="center"
                      gap="3"
                      style={{
                        flex: "0 0 auto",
                        marginLeft: "auto",
                        padding: "4px 8px",
                        borderRadius: 999,
                        background: "var(--gray-a2)",
                        border: "1px solid var(--gray-a4)",
                      }}
                    >
                      <Text size="1" color={smsMetrics.overLimit ? "red" : "gray"}>{smsMetrics.characterCount}/{smsMetrics.segmentCharacterLimit} text</Text>
                      <Box style={{ padding: "2px 8px", borderRadius: 999, background: "var(--gray-a3)" }}>
                        <Text size="1" weight="medium">{attachmentLabel}</Text>
                      </Box>
                      <Tooltip content={smsMetricsTooltip}>
                        <Box style={{ display: "flex", alignItems: "center", color: "var(--gray-9)" }}>
                          <InfoCircledIcon width={15} height={15} />
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>

              <Separator size="4" style={{ minHeight: "1px" }} />
              <Flex p="4" gap="2" style={{ alignSelf: "stretch" }}>
                <Button
                  variant="soft"
                  color="red"
                  disabled={!hasUnsavedChanges || isUpdating}
                  onClick={handleDiscard}
                  style={{ flex: 1 }}
                >
                  Discard changes
                </Button>
                <Button
                  variant="solid"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || !existingTemplate || !formData.name.trim() || !formData.senderName.trim() || !formData.message.trim() || smsMetrics.overLimit || isUpdating}
                  style={{ flex: 1 }}
                >
                  {isUpdating ? "Saving..." : "Save changes"}
                </Button>
              </Flex>
            </Flex>
          </Card>

          <Card style={{ flex: "1 0 0", minWidth: 0, overflow: "hidden" }}>
            <Flex direction="column" style={{ height: "100%" }}>
              <Flex justify="between" align="center" px="3" pt="3" pb="1">
                <Text size="2" weight="medium">Preview</Text>
              </Flex>

              <Flex align="center" justify="center" style={{ flex: "1 0 0", minHeight: 0, padding: "0 var(--space-2) var(--space-2)" }}>
                <MarketingSmsPreview
                  attachmentImageUrl={attachmentImagePreview}
                  message={formData.message}
                  senderName={formData.senderName}
                  includeOptOut
                />
              </Flex>
            </Flex>
          </Card>
        </Flex>
      </Flex>

      <ToastAlert />
    </>
  );
}